'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { XOFFI_MENU_SIDEBAR, XOFFI_MENU_COMPACT } from '@/config/xoffi-menu.config';
import { getMenuIcon } from '@/lib/menu-icons';

// NOTE: Old flattening functions removed - they broke the menu hierarchy!
// The menu now properly shows nested children up to 4+ levels deep.

// Menu Context
const MenuContext = createContext();

// Konvertiere Datenbank-Menü zu Frontend-Format (Metronic-kompatibel)
function convertDbMenuToFrontend(dbMenu, userLang = 'de', userOrg = null) {
  if (!dbMenu || !Array.isArray(dbMenu)) {
    console.error('[convertDbMenuToFrontend] Invalid input - not an array:', dbMenu);
    return [];
  }

  // Convert all items with localized titles (preserves full hierarchy)
  return convertMenuItems(dbMenu, userLang, userOrg);
}

/**
 * Get localized menu item name based on user language
 * Falls back: name_{lang} -> name_de -> name
 */
function getLocalizedName(item, lang = 'de') {
  const langKey = `name_${lang}`;
  return item[langKey] || item.name_de || item.name || '';
}

/**
 * Replace placeholders in menu link with actual values.
 *
 * NOTE: We deliberately no longer substitute `{org}` with the user's organisation
 * — orgs must come from the authenticated session (JWT/auth-context), never
 * from URL paths. Menu link templates that still contain `{org}/<rest>` get the
 * `{org}/` segment stripped so the resulting URL has no org-id in it.
 */
function replaceLinkPlaceholders(link /*, userOrg */) {
  if (!link || typeof link !== 'string') return link;
  return link.replace(/\{org\}\//g, '').replace(/\{org\}/g, '');
}

// Convert individual menu items
function convertMenuItems(dbMenu, userLang = 'de', userOrg = null) {
  return dbMenu.map((item, index) => {
    // Handle header items (legacy type=header)
    if (item.type === 'header') {
      return {
        heading: getLocalizedName(item, userLang),
        gruppe: item.gruppe,
        type: 'header'
      };
    }

    // Handle is_heading from Menu Builder (new display option)
    // If heading has children, treat as collapsible parent (e.g. marketplace module menus)
    if (item.is_heading === 1 || item.is_heading === true) {
      if (item.children && item.children.length > 0) {
        // Collapsible parent with children — fall through to normal item processing
      } else {
        // Heading-only label (no children, no link). MUST preserve show_when so
        // the sidebar's manifest-driven visibility filter can hide these per-entity.
        // Without this, e.g. `#{entity_name}#` separators for kaie-ie AND kaie-hie
        // both render → entity name appears twice in the sidebar.
        return {
          heading: getLocalizedName(item, userLang),
          type: 'header',
          id: item.id,
          module: item.module,
          show_when: item.show_when || null,
          manifest_key: item.manifest_key || null,
          anchor_group: item.anchor_group || null,
        };
      }
    }

    const localizedTitle = getLocalizedName(item, userLang);
    const menuItem = {
      title: localizedTitle,
      target: item.window === '_blank' ? '_blank' : undefined,
      module: item.module,
      id: item.id,
      sequence: item.sequence,
      link_type: item.link_type,
      original_link: item.link,
      // Display options from Menu Builder
      badge_text: item.badge_text ?? null,
      divider_before: item.divider_before === 1 || item.divider_before === true,
      // Manifest-driven conditional visibility (2026-05-12)
      // Frontend filters in SidebarMenu.buildMenu() based on EntityContext.selectedEntity
      show_when: item.show_when || null,
      manifest_key: item.manifest_key || null,
      anchor_group: item.anchor_group || null,
    };

    // Handle is_disabled from Menu Builder (shows "Soon" badge)
    if (item.is_disabled === 1 || item.is_disabled === true) {
      menuItem.disabled = true;
    }

    // Icon bestimmen: bevorzugt DB-Icon, sonst Titel-Alias
    const iconKey = (typeof item.icon === 'string' && item.icon.trim() !== '')
      ? item.icon
      : (typeof item.name === 'string' ? item.name : '');
    if (iconKey) {
      menuItem.icon = getMenuIcon(iconKey);
    }


    // Behandle Link-Routing basierend auf link_type
    // link_type Werte:
    // 0 = Xoffi (interne Next.js Route)
    // 1 = Baseportal Link
    // 2 = Baseportal Include
    // 3 = Kaie Link
    // 4 = Kaie Include
    // 5 = Orthotex Link
    // 6 = Orthotex Include
    // 7 = Wasserweik (Legacy Laravel)
    // 10 = LARAVEL (direkt über /xsite/10/)
    // 100 = Überschrift (keine Navigation)
    // null/"" = Default Legacy Laravel über /xsite/10/curlsite/backend/

    const linkType = item.link_type !== null && item.link_type !== undefined
      ? parseInt(item.link_type, 10)
      : null;

    // Replace placeholders in link (e.g. {org} -> user's organisation)
    const processedLink = replaceLinkPlaceholders(item.link, userOrg);

    // WICHTIG: Zuerst prüfen ob link="#" oder leer - diese sind Parent-Menüs ohne eigenen Link
    if (!processedLink || processedLink === '#' || processedLink === '') {
      // Parent-Menü ohne eigenen Link (nur zum Aufklappen für Children)
      menuItem.path = undefined;
      menuItem.isExternal = false;
      menuItem.link_type = linkType;
    } else if (linkType === 0) {
      // XOFFI - Interne Next.js Route (direkte Navigation)
      const path = processedLink?.startsWith('/') ? processedLink : `/${processedLink}`;
      menuItem.path = path;
      menuItem.isExternal = false;
      menuItem.link_type = 0;
    } else if (linkType === 10) {
      // LARAVEL - Server Route über DE/WEB (für Browser-URL-Anzeige)
      menuItem.path = `/xsite/10/${processedLink}`;
      menuItem.isExternal = true;
      menuItem.method = 'GET';
      menuItem.link_type = 10;
      menuItem.menuTitle = localizedTitle;
    } else if (linkType === 8) {
      // React - Interne Next.js Route (direktes Routing)
      const path = processedLink?.startsWith('/') ? processedLink : `/${processedLink}`;
      menuItem.path = path;
      menuItem.isExternal = false;
      menuItem.link_type = 8;
    } else if (linkType === 11) {
      // Module Page — MUST route to the full-layout shell /app/{slug}/... (header
      // + sidebar + iframe), NOT the raw /modules/{slug}/... iframe content
      // (see rule-details/module-url-patterns.md). Handles both link conventions:
      //   - newer installs store the fully-prefixed link "/app/{slug}/kb"  → use as-is
      //   - legacy installs store a bare sub-path "/admin" / "admin" / "/"  → prefix here
      // The old code did `/modules/{slug}/` + link, which double-prefixed the
      // newer "/app/..." links into "/modules/{slug}//app/{slug}/kb".
      const moduleSlug = item.module || ''
      const rawLink = item.link || ''
      if (rawLink.startsWith('/app/')) {
        menuItem.path = rawLink
      } else if (rawLink.startsWith('http')) {
        menuItem.path = rawLink
        menuItem.isExternal = true
      } else {
        const suffix = rawLink.replace(/^\/+/, '')
        menuItem.path = `/app/${moduleSlug}${suffix ? '/' + suffix : ''}`
      }
      menuItem.isExternal = menuItem.isExternal || false
      menuItem.link_type = 11
      menuItem.moduleSlug = moduleSlug
    } else if (linkType === 100) {
      // Überschrift - keine Navigation
      menuItem.path = undefined;
      menuItem.isExternal = false;
      menuItem.link_type = 100;
    } else if (linkType === null || linkType === 7 || item.link_type === "") {
      // Default Legacy: Laravel Server Route über /xsite/10/curlsite/backend/
      menuItem.path = `/xsite/10/curlsite/backend/${processedLink}`;
      menuItem.isExternal = true;
      menuItem.method = 'GET';
      menuItem.link_type = linkType;
      menuItem.menuTitle = localizedTitle;
    } else {
      // Andere link_types (1-6) - vorerst als interne Links behandeln
      menuItem.path = processedLink;
      menuItem.isExternal = false;
      menuItem.link_type = linkType;
    }
    // Überschrift/Separator (kein Link UND keine Kinder)
    if (!item.link && !item.children) {
      menuItem.heading = localizedTitle;
      delete menuItem.title;
      delete menuItem.path;
      delete menuItem.icon;
    }

    // IMMER children verarbeiten wenn vorhanden - UNABHÄNGIG vom Link!
    if (item.children && item.children.length > 0) {
      menuItem.children = convertMenuItems(item.children, userLang, userOrg);
    }
    // NOTE: Removed automatic disabled logic for items without links
    // Items are now ONLY disabled if explicitly set via is_disabled from Menu Builder


    return menuItem;
  });
}

export function MenuProvider({ children }) {
  const [menuData, setMenuData] = useState(null);
  const [headerMenu, setHeaderMenu] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useFallback, setUseFallback] = useState(false);
  const [organisation, setOrganisation] = useState(null);

  // Lade Menü-Daten vom Backend
  const loadMenuData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Token aus Cookie oder localStorage lesen und als Authorization mitsenden
      const getCookie = (name) => {
        try {
          const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()\[\]\\/\+^]/g, '\\$&') + '=([^;]*)'));
          return match ? decodeURIComponent(match[1]) : '';
        } catch {
          return '';
        }
      };
      const cookieToken = getCookie('auth-token') || getCookie('token');
      const lsToken = (typeof localStorage !== 'undefined') ? (localStorage.getItem('auth-token') || localStorage.getItem('auth_token') || '') : '';
      const token = cookieToken || lsToken || '';

      // Ohne Token kein Fetch: /api/menu/structure liefert unauthentifiziert
      // ohnehin nur 401→Fallback, und kein pre-login-Screen rendert ein Menü.
      // Nach Login remountet der Provider durch den Hard-Reload mit Token.
      if (!token) {
        setIsLoading(false);
        return;
      }

      const response = await fetch('/api/menu/structure', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      if (result.fallback) {
        setUseFallback(true);
        setMenuData(XOFFI_MENU_SIDEBAR);
        setHeaderMenu([]);
      } else if ((result && result.data && Array.isArray(result.data.menu)) || (result && Array.isArray(result.menu))) {
        // Get user language and organisation from localStorage
        let userLang = 'de';
        let userOrg = null;
        try {
          const userData = localStorage.getItem('user_data');
          if (userData) {
            const user = JSON.parse(userData);
            userLang = user?.default_lang || 'de';
            // Get user's organisation for placeholder replacement (e.g. {org} in menu links)
            userOrg = user?.organisation || user?.kontakt_organisation || null;
          }
        } catch (e) {
          console.warn('[MenuProvider] Could not read user data, using defaults');
        }

        // Konvertiere Datenbank-Format zu Frontend-Format mit User-Sprache und Organisation
        const dbMenu = result.data?.menu || result.menu || [];
        const frontendMenu = convertDbMenuToFrontend(dbMenu, userLang, userOrg);

        // Header-Menü konvertieren ZUERST (unabhängig vom Side-Menu!)
        const dbHeaderMenu = result.data?.header_menu || [];
        const frontendHeader = convertDbMenuToFrontend(dbHeaderMenu, userLang, userOrg);

        // Header-Menu immer setzen wenn vorhanden (z.B. Prohandel hat NUR header_menu)
        if (frontendHeader && frontendHeader.length > 0) {
          setHeaderMenu(frontendHeader);
        } else {
          setHeaderMenu([]);
        }

        // Side-Menu separat behandeln
        if (frontendMenu && frontendMenu.length > 0) {
          setMenuData(frontendMenu);
          setOrganisation(result.data?.organisation || null);
        } else if (frontendHeader && frontendHeader.length > 0) {
          // Prohandel-Fall: Nur Header-Menu, kein Side-Menu
          setMenuData([]);  // Leeres Side-Menu
          setOrganisation(result.data?.organisation || null);
          // KEIN Fallback - wir haben ja Header-Menu!
        } else {
          setUseFallback(true);
          setMenuData(XOFFI_MENU_SIDEBAR);
        }
      } else {
        throw new Error('Invalid response format');
      }

    } catch (err) {
      setError(err.message);
      setUseFallback(true);
      setMenuData(XOFFI_MENU_SIDEBAR);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial Load
  useEffect(() => {
    loadMenuData();
  }, []);

  // Listen for global menu-refresh events (fired after marketplace install/uninstall etc.)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => loadMenuData();
    window.addEventListener('xoffi:menu-refresh', handler);
    return () => window.removeEventListener('xoffi:menu-refresh', handler);
  }, []);

  // Retry-Funktion
  const retryLoadMenu = () => {
    loadMenuData();
  };

  // Menü-Getter für verschiedene Varianten
  const getMenu = (variant = 'full') => {
    if (variant === 'compact') {
      return useFallback ? XOFFI_MENU_COMPACT : (menuData ? menuData.slice(0, 10) : []);
    }
    return menuData || [];
  };

  // Context Value
  const contextValue = {
    // Daten
    menuData: getMenu(),
    headerMenu: headerMenu || [],
    compactMenu: getMenu('compact'),
    
    // Status
    isLoading,
    error,
    useFallback,
    organisation,
    
    // Funktionen
    retryLoadMenu,
    getMenu,
    
    // Debug-Info
    debug: {
      source: useFallback ? 'static-fallback' : 'database',
      itemCount: menuData ? menuData.length : 0,
      organisation: organisation || 'unknown',
      headerItemCount: headerMenu ? headerMenu.length : 0
    }
  };

  return (
    <MenuContext.Provider value={contextValue}>
      {children}
    </MenuContext.Provider>
  );
}

// Hook für Menu-Verwendung
export function useMenu() {
  const context = useContext(MenuContext);
  
  if (!context) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  
  return context;
}

// Convenience Hook für spezifische Menu-Varianten
export function useMenuData(variant = 'full') {
  const { getMenu, isLoading, error, useFallback } = useMenu();
  
  return {
    menuData: getMenu(variant),
    isLoading,
    error,
    useFallback
  };
}

// Debug Hook
export function useMenuDebug() {
  const { debug, useFallback, error, organisation } = useMenu();
  
  return {
    ...debug,
    useFallback,
    error,
    organisation,
    timestamp: new Date().toISOString()
  };
}